import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SideBar from '../../components/Topbar';
import { HTMLCourseNavProvider } from "../../components/SidebarCourseContext";
import './assets/styles/HTMLLesson1.css';
import { CopyBlock, dracula } from "react-code-blocks";

// Import sophisticated icons
import { 
  FaCode,
  FaCheck,
  FaArrowRight,
  FaPlay,
  FaBookOpen,
  FaFileCode,
  FaEdit,
  FaLightbulb
} from 'react-icons/fa';

const HTMLLesson1_1 = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [viewedSections, setViewedSections] = useState(new Set([0]));
  const sectionRefs = useRef([]);
  const observerRef = useRef(null);

  const [language] = useState("html");
  const [lineNumbers] = useState(true);
  const code = `<!DOCTYPE html>
<html>
<head>
    <title>My Simple Web Page</title>
</head>
<body>
    <p>Welcome to my webpage</p>
</body>
</html>`;

  // Video explanation URL
  const videoUrl = "https://app.codeflarelimited.com/watch-video/html-course";

  const sections = [
    {
      title: "Objective & Requirements",
      content: "By the end of this lesson, you will be able to create a basic HTML document and view it in your web browser.",
      requirements: [
        "Any text editor (VS Code, Sublime Text, Atom, Notepad++, or even Notepad)",
        "A modern browser (Chrome, Firefox, Edge, Safari)"
      ],
      icon: <FaLightbulb className="section-icon" />,
      color: "#2563eb"
    },
    {
      title: "Creating Your First File", 
      content: "Learn how to create and save your first HTML file with proper naming conventions",
      steps: [
        "Open your text editor",
        "Create a new file and save it as index.html (or any name ending with .html)"
      ],
      icon: <FaEdit className="section-icon" />,
      color: "#1d4ed8"
    },
    {
      title: "HTML5 Template Structure",
      content: "Understand the minimal valid HTML5 template and its essential components",
      icon: <FaFileCode className="section-icon" />,
      color: "#1e40af"
    }
  ];

  // Intersection Observer for scroll detection
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '-10% 0px -20% 0px',
      threshold: 0.6
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const index = sectionRefs.current.indexOf(entry.target);
        
        if (entry.isIntersecting) {
          setActiveSection(index);
          setViewedSections(prev => new Set([...prev, index]));
        }
      });
    }, options);

    sectionRefs.current.forEach((section) => {
      if (section) {
        observerRef.current.observe(section);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const addSectionRef = (element, index) => {
    sectionRefs.current[index] = element;
  };

  const getCompletionPercentage = () => {
    const totalSections = sections.length + 2;
    const viewedCount = viewedSections.size;
    return Math.round((viewedCount / totalSections) * 100);
  };

  return (
    <HTMLCourseNavProvider>
      <div className="html-lesson-container">
        <SideBar />
        
        <div className="html-lesson-main">
          {/* Header Section */}
          <div className="html-lesson-header">
            <div className="html-header-content">
              <div className="html-lesson-badge">
                <FaBookOpen className="html-badge-icon" />
                Lesson 1.1 • {getCompletionPercentage()}% Viewed
              </div>
              <h1 className="html-lesson-title">Creating Your First HTML Document</h1>
              <p className="html-lesson-subtitle">
                Learn to build and understand the structure of a basic HTML5 document
              </p>
              
              {/* Progress Bar */}
              <div className="html-completion-progress">
                <div 
                  className="html-completion-fill"
                  style={{ width: `${getCompletionPercentage()}%` }}
                ></div>
                <div className="html-progress-labels">
                  <span>Getting Started</span>
                  <span>Complete</span>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="html-progress-indicator">
            {sections.map((section, index) => (
              <div 
                key={index}
                className={`html-progress-step ${viewedSections.has(index) ? 'html-viewed' : ''} ${index === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-step-number">
                  {viewedSections.has(index) ? '✓' : index + 1}
                </div>
                <div className="html-step-label">{section.title}</div>
                {index < sections.length - 1 && <div className="html-step-connector"></div>}
              </div>
            ))}
          </div>

          {/* Content Sections */}
          <div className="html-content-sections">
            {/* Section 1: Objective & Requirements */}
            <div 
              key={0}
              ref={(el) => addSectionRef(el, 0)}
              className={`html-content-card ${viewedSections.has(0) ? 'html-viewed' : ''} ${0 === activeSection ? 'html-active' : ''}`}
            >
              <div className="html-card-badge">
                {viewedSections.has(0) ? '✓ Viewed' : 'Objective'}
              </div>
              
              <div className="html-card-header">
                <div className="html-card-icon" style={{ backgroundColor: sections[0].color }}>
                  {sections[0].icon}
                </div>
                <div className="html-card-text">
                  <h2 className="html-card-title">{sections[0].title}</h2>
                  <p className="html-card-content">{sections[0].content}</p>
                  
                  <div className="html-requirements">
                    <h4>What You Need:</h4>
                    <ul>
                      {sections[0].requirements.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="html-card-actions">
                <a 
                  href={videoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="html-action-btn"
                >
                  <FaPlay className="html-btn-icon" />
                  Watch Video Explanation
                </a>
              </div>
            </div>

            {/* Section 2: Creating File */}
            <div 
              key={1}
              ref={(el) => addSectionRef(el, 1)}
              className={`html-content-card ${viewedSections.has(1) ? 'html-viewed' : ''} ${1 === activeSection ? 'html-active' : ''}`}
            >
              <div className="html-card-badge">
                {viewedSections.has(1) ? '✓ Viewed' : 'Step-by-Step'}
              </div>
              
              <div className="html-card-header">
                <div className="html-card-icon" style={{ backgroundColor: sections[1].color }}>
                  {sections[1].icon}
                </div>
                <div className="html-card-text">
                  <h2 className="html-card-title">{sections[1].title}</h2>
                  <p className="html-card-content">{sections[1].content}</p>
                  
                  <div className="html-steps">
                    <h4>Steps to Create:</h4>
                    <ul>
                      {sections[1].steps.map((step, idx) => (
                        <li key={idx}>{step}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="html-card-actions">
                <a 
                  href={videoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="html-action-btn"
                >
                  <FaPlay className="html-btn-icon" />
                  Watch Video Tutorial
                </a>
              </div>
            </div>

            {/* Section 3: HTML5 Template */}
            <div 
              key={2}
              ref={(el) => addSectionRef(el, 2)}
              className={`html-content-card ${viewedSections.has(2) ? 'html-viewed' : ''} ${2 === activeSection ? 'html-active' : ''}`}
            >
              <div className="html-card-badge">
                {viewedSections.has(2) ? '✓ Viewed' : 'Template'}
              </div>
              
              <div className="html-card-header">
                <div className="html-card-icon" style={{ backgroundColor: sections[2].color }}>
                  {sections[2].icon}
                </div>
                <div className="html-card-text">
                  <h2 className="html-card-title">{sections[2].title}</h2>
                  <p className="html-card-content">{sections[2].content}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Code Example */}
          <div 
            className="html-code-example"
            ref={(el) => addSectionRef(el, sections.length)}
          >
            <div className="html-code-header">
              <div className="html-code-badge">
                <FaCode className="html-badge-icon" />
                Minimal HTML5 Template
              </div>
              <h3 className="html-code-title">
                Copy and paste this code to get started
              </h3>
            </div>
            <div className="html-code-block">
              <CopyBlock
                language={language}
                text={code}
                showLineNumbers={lineNumbers}
                theme={dracula}
                wrapLines={true}
                codeBlock
              />
            </div>
            <div className="html-code-actions">
              <a 
                href={videoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="html-code-btn html-primary"
              >
                Watch Full Video Tutorial
              </a>
            </div>
          </div>

          {/* Template Explanation */}
          <div 
            className="html-content-card html-explanation-card"
            ref={(el) => addSectionRef(el, sections.length + 1)}
          >
            <div className="html-card-header">
              <div className="html-card-text">
                <h2 className="html-card-title">Understanding the HTML Structure</h2>
                <p className="html-card-content">Each element in the template serves a specific purpose:</p>
                
                <div className="html-explanation-list">
                  <div className="html-explanation-item">
                    <div className="html-explanation-number">1</div>
                    <div className="html-explanation-content">
                      <strong>&lt;!DOCTYPE html&gt;</strong> - Defines this document as HTML5
                    </div>
                  </div>
                  <div className="html-explanation-item">
                    <div className="html-explanation-number">2</div>
                    <div className="html-explanation-content">
                      <strong>&lt;html&gt;</strong> - The root element of an HTML page
                    </div>
                  </div>
                  <div className="html-explanation-item">
                    <div className="html-explanation-number">3</div>
                    <div className="html-explanation-content">
                      <strong>&lt;title&gt;</strong> - Specifies a title for the document
                    </div>
                  </div>
                  <div className="html-explanation-item">
                    <div className="html-explanation-number">4</div>
                    <div className="html-explanation-content">
                      <strong>&lt;body&gt;</strong> - Contains the visible page content
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="html-card-actions">
              <a 
                href={videoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="html-action-btn"
              >
                <FaPlay className="html-btn-icon" />
                Watch Detailed Explanation
              </a>
            </div>
          </div>

          {/* Action Section */}
          <div 
            className="html-action-section"
            ref={(el) => addSectionRef(el, sections.length + 2)}
          >
            <div className="html-action-header">
              <h3>Ready to create your first webpage?</h3>
              <div className="html-completion-badge">
                {getCompletionPercentage()}% of content viewed
              </div>
            </div>
            
            <div className="html-action-content">
              <p>You've learned the basics of HTML document structure. Practice creating your own file!</p>
              
              <div className="html-action-buttons">
                <button 
                  className={`html-complete-button ${isCompleted ? 'html-completed' : ''}`}
                  onClick={() => setIsCompleted(true)}
                >
                  {isCompleted ? (
                    <>
                      <FaCheck className="html-button-icon" />
                      Lesson Completed
                    </>
                  ) : (
                    'Mark as Complete'
                  )}
                </button>
                
                {isCompleted && (
                  <Link to="/html-tags" className="html-next-lesson-link">
                    Continue to Next Lesson
                    <FaArrowRight className="html-link-icon" />
                  </Link>
                )}
              </div>
              
              <div className="html-quick-nav">
                <span>Need help? </span>
                <a 
                  href={videoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="html-nav-link"
                >
                  Watch Full Video Course
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </HTMLCourseNavProvider>
  );
}

export default HTMLLesson1_1;